import React from 'react';

import { Box, Stack, Typography } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { faRoute, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

import { MapSettingIcon } from '../components/MapSettingIcon';

import { activateLayer, deactivateLayer } from '../store/actions/layerActions';
import { setShowActiveGraphLayer } from '../store/actions/mapActions';
import { CustomLayerButton } from '../components/Button/CustomButton';
import { FontAwesomeSvgIcon } from '../components/FontAwesomeSvgIcon';
import { activateObstacleLayer, deactivateObstacleLayer } from '../store/actions/obstacleActions';

export const MapSettingButton = ({id, active, value, label, ariaLabelHidden, ariaLabelShowed, whiteIcon, blackIcon}) => {
  const [checked, setChecked] = React.useState(active);
  const dispatch = useDispatch();
  const addLayerActive = (event,layer) => {
    event.preventDefault();
    dispatch(activateLayer(layer));
    setChecked(true);
  }; 
  const removeLayerActive = (event, layer) => {
    event.preventDefault();
    dispatch(deactivateLayer(layer));
    setChecked(false);
  };

  return (
    <MapSettingIcon 
      id={id}
      value={value} 
      label={label} 
      ariaLabel={checked ? ariaLabelShowed: ariaLabelHidden} 
      icon={checked ? whiteIcon: blackIcon}
      active={checked} 
      setAction={checked ? event => removeLayerActive(event, value): event => addLayerActive(event,value)}
    />
  );
};

const ButtonShowGraph = ({
  label,
  showBaseGraph,
  onSetShowGraph, 
  ariaLabelShowed,
  ariaLabelHidden
}) => {
  const styles = {
    buttonContainer: {
      display: 'block', 
      marginRight: '0.6em', 
      marginTop: '0.6em'
    },
    labelButton: {
      fontStyle: 'normal',
      fontSize: '1em',
    }
  };
  return (
    <Box component='li' sx={styles.buttonContainer} key='voirie'>
      <CustomLayerButton 
        id='voirie_button' 
        label='Voirie' 
        variant='contained'
        activebutton={showBaseGraph ? 'true' : 'false'}
        onClick={onSetShowGraph}
        icon={faRoute}
        aria-label={showBaseGraph ? ariaLabelShowed: ariaLabelHidden} 
        startIcon={
          <FontAwesomeSvgIcon 
            icon={faRoute}
            sx={{
              width: 24, 
              height: 24, 
              color: showBaseGraph ? '#FFFFFF' : '#000000'
            }}
          />
        }
      >
        <Typography aria-hidden={true} sx={styles.labelButton}>
          {label}
        </Typography>
      </CustomLayerButton>
    </Box>
  );
};

const ButtonShowObstacles = ({
  label,
  showObstacles,
  onSetShowObstacles, 
  ariaLabelShowed,
  ariaLabelHidden
}) => {
  const styles = {
    buttonContainer: {
      display: 'block', 
      marginRight: '0.6em', 
      marginTop: '0.6em'
    },
    labelButton: {
      fontStyle: 'normal',
      fontSize: '1em',
    }
  };
  return (
    <Box component='li' sx={styles.buttonContainer} key='obstacle'>
      <CustomLayerButton 
        id='obstacle_button' 
        label='Obstacle' 
        variant='contained'
        activebutton={showObstacles ? 'true' : 'false'}
        onClick={onSetShowObstacles}
        icon={faExclamationTriangle}
        aria-label={showObstacles ? ariaLabelShowed: ariaLabelHidden} 
        startIcon={
          <FontAwesomeSvgIcon 
            icon={faExclamationTriangle}
            sx={{
              width: 24, 
              height: 24, 
              color: showObstacles ? '#FFFFFF' : '#000000'
            }}
          />
        }
      >
        <Typography aria-hidden={true} sx={styles.labelButton}>
          {label}
        </Typography>
      </CustomLayerButton>
    </Box>
  );
}; 

export const SettingBar = ({direction, nbButton}) => {
  const layersActive = useSelector(state => state.layerConf.buttonsLayersState);
  const showBaseGraph = useSelector(state => state.map.showBaseGraphe);
  const showObstacles = useSelector(state => state.obstacle.showLayer);
  const obstaclesFeatures = useSelector(state => state.obstacle.obstaclesFeatures);
  const buttonShowed = Object.entries(layersActive).length !== 0 && nbButton !== undefined ? Object.keys(layersActive).slice(0, nbButton): Object.keys(layersActive);
  const { i18n, t } = useTranslation();
  const getLanguage = () => i18n.language || window.localStorage.i18nextLng;
  const dispatch = useDispatch();
  const onSetShowGraph = () => {
    dispatch(setShowActiveGraphLayer(!showBaseGraph));
  };

  const styles = {
    container : {
      display:'flex',
      flexWrap: 'wrap',
      maxWidth: '1100px',
      marginBlockStart: 0,
      marginBlockEnd: 0,
      paddingInlineStart: 0
    },
    buttonContainer : {
      display: 'block', 
      marginRight: '0.6em', 
      marginTop: '0.6em'
    }
  };

  return (
    <Stack 
      component="ul" 
      sx={styles.container} 
      direction={direction ?? 'row'} 
    >
      <ButtonShowGraph 
        showBaseGraph={showBaseGraph} 
        onSetShowGraph={onSetShowGraph} 
        label={t('layer.roadway-layer')}
        ariaLabelHidden={t('layer.hide-layer-roadway')}
        ariaLabelShowed={t('layer.show-layer-roadway')}
      />
      {Object.entries(layersActive).length !== 0 && buttonShowed.map(layer => {
        // Do not create a button if no icon (used when layers are always dispayed on the map)
        // We should filter active layer before computing the number of buttons
        if (layersActive[layer].whitePngIcon !== '' && layersActive[layer].blackPngIcon !== '') {
          const translatedLabel = layersActive[layer].label;
          const translatedAriaLabelHidden = getLanguage()==='fr' ? `Afficher la couche ${layersActive[layer].label}`: `Show the layer ${layersActive[layer].label}`;
          const translatedAriaLabelShowed = getLanguage()==='fr' ? `Cacher la couche ${layersActive[layer].label}`: `Hide the layer ${layersActive[layer].label}`;
          return (
            <Box component='li' sx={styles.buttonContainer} key={layer}>
              <MapSettingButton 
                id={`${layer}_button`}
                active={layersActive[layer].active}
                value={layer} 
                label={translatedLabel}
                ariaLabelHidden={translatedAriaLabelHidden}
                ariaLabelShowed={translatedAriaLabelShowed}
                whiteIcon={layersActive[layer].whitePngIcon} 
                blackIcon={layersActive[layer].blackPngIcon}
              />
            </Box>
          );
        }
      })}
      {obstaclesFeatures && (
        <ButtonShowObstacles 
          showObstacles={showObstacles}
          label={t(('layer.obstacles-layer'))}
          onSetShowObstacles={showObstacles ? () => dispatch(deactivateObstacleLayer()) : () => dispatch(activateObstacleLayer())}
          ariaLabelHidden={t('layer.hide-layer-obstacles')}
          ariaLabelShowed={t('layer.show-layer-obstacles')}
        />
      )}
       
    </Stack>
  );
};